import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { STAY_PLAN_TABLES } from "@/lib/daycare-hotel/stayPlans";
import { buildChargePeriod } from "../plans/stayPlanContractDraft";
import type { ReservationFormValues } from "./reservationForm";
import type { AutomaticStayLine } from "./reservationBilling";
import { expandAutomaticStayCharges } from "./stayCoveragePersistence";
import { useOpenStayComanda } from "./useOpenStayComanda";
import {
  buildReservationPayload,
  buildStayComandaLines,
  buildStayPlanHolds,
  getHotelReservationNights,
} from "./stayReservationPersistence";
import { invalidateStayReservationQueries } from "./stayReservationQueryInvalidation";
import { materializeStayDays } from "./useMaterializeStayDays";
import { buildStayDayDates } from "@/lib/daycare-hotel/stayDays";
import { buildStayReservationPackRows } from "./reservationPacks";
import { stayDaySourceFromValues } from "./reservationDates";

export interface CreateReservationInput {
  values: ReservationFormValues;
  owner: { id?: string; phone: string | null } | null;
  automaticLines: AutomaticStayLine[];
}

export function useCreateStayReservation() {
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("daycareHotel");
  const { openComanda } = useOpenStayComanda();

  return useMutation({
    mutationFn: async ({ values, owner, automaticLines }: CreateReservationInput) => {
      if (!profile?.current_clinic_id) throw new Error("no_clinic_selected");
      const clinicId = profile.current_clinic_id;
      if (values.moduleType === "hotel" && getHotelReservationNights(values) < 1) {
        throw new Error("hotel_nights_required");
      }
      const payload = buildReservationPayload(values, clinicId);
      const dateSource = stayDaySourceFromValues(values);
      const { data, error } = await (supabase as any).from("stay_reservations").insert(payload).select("id").single();
      if (error) throw error;

      const packRows = buildStayReservationPackRows(data.id, values.stayPackIds);
      if (packRows.length > 0) {
        const { error: packError } = await (supabase as any)
          .from("stay_reservation_packs")
          .insert(packRows);
        if (packError) throw packError;
      }

      // Com chip removido a regra não descreve mais as datas: as diárias guardam o que vale.
      if (values.recurrence && dateSource.recurrence && (dateSource.excludedDates ?? []).length === 0) {
        const usesWeekdays = values.recurrence.frequency === "weekly" || values.recurrence.frequency === "biweekly";
        const { error: recurrenceError } = await (supabase as any).from("stay_reservation_recurrence").insert({
          stay_reservation_id: data.id,
          frequency: values.recurrence.frequency,
          days_of_week: usesWeekdays ? values.recurrence.weekdays : null,
          days_of_month: values.recurrence.frequency === "monthly" && values.recurrence.monthday ? [values.recurrence.monthday] : null,
          interval_days: values.recurrence.frequency === "biweekly" ? 14 : values.recurrence.interval,
          ends_on: values.recurrence.endsOn,
        });
        if (recurrenceError) throw recurrenceError;
      }

      await materializeStayDays({
        reservationId: data.id,
        clinicId,
        petId: values.petId,
        moduleType: payload.module_type as "daycare" | "hotel" | "both",
        checkInDate: values.checkInDate,
        checkOutDate: values.checkOutDate ?? null,
        checkInTime: null,
        checkOutTime: null,
        period: null,
        hotelRoomId: values.hotelRoomId ?? null,
        hotelRoomBedId: values.hotelRoomBedId ?? null,
        recurrence: dateSource.recurrence,
        pickedDates: dateSource.pickedDates,
        excludedDates: dateSource.excludedDates,
      });

      // A reserva SEGURA a unidade do plano (hold); o débito de verdade só
      // acontece no check-in (useCheckInReservation). O plano em si é
      // contratado à parte (ficha do pet ou StayPlanContractDialog na
      // reserva) — a reserva só consome um contrato já existente.
      const module: StayPlanModule = values.moduleType === "hotel" ? "hotel" : "daycare";
      const contractId: string | null = values.stayPlanContractId ?? null;

      if (contractId) {
        const occurrences = buildStayDayDates({
          ...dateSource,
          moduleType: payload.module_type as "daycare" | "hotel" | "both",
        });
        const holds = buildStayPlanHolds({ values, occurrences });
        for (const hold of holds) {
          const { error: holdError } = await (supabase as any).rpc("consume_stay_plan_units", {
            p_module: module,
            p_contract_id: contractId,
            p_reservation_id: data.id,
            p_used_on: hold.usedOn,
            p_units: hold.units,
            p_source: "hold",
          });
          if (holdError) throw holdError;
        }
      }

      // Abre a comanda POS da estadia com as linhas avulsas informadas.
      const comandaLines = [
        ...buildStayComandaLines(values.comanda ?? []),
        ...expandAutomaticStayCharges(automaticLines),
      ];
      const { saleId: comandaSaleId, insertedLines } = await openComanda({
        clinicId,
        reservationId: data.id,
        petId: values.petId,
        ownerId: owner?.id ?? values.ownerId ?? null,
        lines: comandaLines,
      });

      for (const insertedLine of insertedLines) {
        if (!insertedLine.stayDate) continue;
        const { error: dayLinkError } = await (supabase as any)
          .from("stay_reservation_days")
          .update({ daily_charge_sale_item_id: insertedLine.id })
          .eq("stay_reservation_id", data.id)
          .eq("stay_date", insertedLine.stayDate)
          .eq("status", "scheduled");
        if (dayLinkError) throw dayLinkError;
      }

      if (values.stayPlanChargeInComanda && contractId) {
        const tables = STAY_PLAN_TABLES[module];
        const { data: contract, error: contractError } = await (supabase as any)
          .from(tables.contracts)
          .select("starts_on, first_due_on, billing_timing, terms_snapshot")
          .eq("id", contractId)
          .single();
        if (contractError) throw contractError;
        // Recorrente é o que não tem diárias fechadas no snapshot; `usage_rule`
        // é nulo no recorrente ilimitado e classificava errado.
        const kind = contract.terms_snapshot?.included_days == null ? "recurring" : "day_package";
        const period = buildChargePeriod({
          kind,
          startsOn: contract.starts_on,
          firstDueOn: contract.first_due_on,
          billingTiming: contract.billing_timing,
        });
        const dueOn = contract.billing_timing === "end" ? period.periodEndsOn : period.periodStartsOn;
        const { error: chargeError } = await (supabase as any).from(tables.charges).insert({
          contract_id: contractId,
          sale_id: comandaSaleId,
          due_on: dueOn,
          period_starts_on: period.periodStartsOn,
          period_ends_on: period.periodEndsOn,
        });
        if (chargeError) throw chargeError;
      }

      return { id: data.id };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["stay-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      if (profile?.current_clinic_id) {
        invalidateStayReservationQueries(queryClient, profile.current_clinic_id, variables.values.checkInDate);
      }
      toast({ title: t("reservationCreated") });
    },
  });
}
